
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useAsyncEffect } from "@/utils/asyncUtils";
import { postFunctionData } from "@/utils/supabaseFunctions";

export interface ForecastData {
  id: string;
  forecast_date: string;
  predicted_amount_cents: number;
  confidence_level: number;
  data_points: {
    average_monthly_revenue: number;
    active_enrollments: number;
    upcoming_invoices_value: number;
    historical_months_analyzed: number;
  };
  factors: string[];
  created_at?: string;
}

interface ForecastResponse {
  success: boolean;
  error?: string;
  forecasts?: ForecastData[];
}

export function useRevenueForecast() {
  const { user, session } = useAuth();
  const [forecasts, setForecasts] = useState<ForecastData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchForecasts = async (forceRefresh = false) => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // First check if we have cached forecasts in the database (unless we're forcing a refresh)
      if (!forceRefresh) {
        const { data, error: fetchError } = await supabase
          .from('revenue_forecasts')
          .select('*')
          .eq('organization_id', user.user_metadata?.organization_id)
          .order('forecast_date', { ascending: true });

        if (fetchError) throw fetchError;
        
        // Check if we already have forecasts and they're relatively fresh (less than 24 hours old)
        if (data && data.length > 0) {
          // Explicitly cast data to ForecastData[] to avoid type errors
          const typedData = data as unknown as ForecastData[];
          
          // Find the newest forecast by created_at date
          const newest = typedData.reduce((latest, current) => {
            if (!current.created_at || !latest.created_at) return latest;
            return new Date(current.created_at) > new Date(latest.created_at) ? current : latest;
          }, typedData[0]);
          
          const createdAt = newest.created_at ? new Date(newest.created_at) : null;
          const isFresh = createdAt && (new Date().getTime() - createdAt.getTime() < 86400000); // 24 hours
          
          if (isFresh) {
            setForecasts(typedData);
            setLastUpdated(createdAt);
            setLoading(false);
            return;
          }
        }
      }
      
      // Verify that we have a valid session before making the edge function call
      if (!session?.access_token) {
        console.warn("No valid session found for edge function call");
        throw new Error("You must be logged in to generate forecasts");
      }
      
      // If no cached data or we're forcing a refresh, generate new forecasts
      try {
        const data = await postFunctionData<ForecastResponse>('generate-revenue-forecast', { 
          months: 3 
        });
        
        if (!data?.success) {
          throw new Error(data?.error || 'Failed to generate forecasts');
        }
        
        // After generating new forecasts, fetch them from the database
        const { data: freshData, error: freshError } = await supabase
          .from('revenue_forecasts')
          .select('*')
          .eq('organization_id', user.user_metadata?.organization_id)
          .order('forecast_date', { ascending: true });
        
        if (freshError) throw freshError;
        
        // Explicitly cast to ForecastData[] to avoid type errors
        setForecasts(freshData as unknown as ForecastData[]);
        setLastUpdated(new Date());
      } catch (functionError: any) {
        console.error("Forecast function error:", functionError);
        toast.error("Couldn't generate forecasts", {
          description: functionError.message || "Please try again later."
        });
        
        // Continue with existing forecasts if available
        if (forecasts.length === 0) {
          setError('Unable to generate new forecasts. Please try again later.');
        }
      }
    } catch (err: any) {
      console.error('Error fetching revenue forecasts:', err);
      setError('Unable to load forecasts. Please try again later.');
      
      // Don't reset forecasts if we have them - keep the old ones
      if (forecasts.length === 0) {
        // Set fallback forecasts only if we don't have any
        setForecasts([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useAsyncEffect(async () => {
    if (user && session?.access_token) {
      await fetchForecasts();
    }
  }, [user, session?.access_token]);

  return {
    forecasts,
    loading,
    error,
    lastUpdated,
    refreshForecasts: () => fetchForecasts(true),
  };
}
